export interface CompanyTeamMember {
  id: string;
  name: string;
  role: string;
}

export interface CompanyManifestoSection {
  id: string;
  description: string;
  title: string;
}

export interface CompanyInvestor {
  id: string;
  name: string;
  media: {
    id: string;
    src: string;
    type: 'image';
  };
}

export interface CompanyPageContent {
  intro: {
    emphasized: string;
    label: string;
    remainder: string;
    supporting: string;
  };
  investors: CompanyInvestor[];
  manifesto: {
    sections: CompanyManifestoSection[];
  };
  team: CompanyTeamMember[];
}

export const companyPageContent: CompanyPageContent = {
  intro: {
    label: 'Company',
    emphasized:
      'We build the kinematic world models and datasets that make embodied AI training tractable at scale',
    remainder:
      ". Spatiotemporal data — motion, objects, environments, physics — is the unsolved layer of the physical AI stack, and it's the entire focus of our lab.",
    supporting:
      "Human movement is the world's most powerful platform and the fastest route to physical AI.",
  },
  manifesto: {
    sections: [
      {
        id: 'model-motion',
        title: 'Model motion, model the world.',
        description: `A person catches a falling glass. Steps over a puddle. Carries a sleeping child.

Every movement puts knowledge of the physical world into practice. We judge weight, anticipate contact, and find our balance. Much of what we know, we know through our bodies.

We believe human movement is the foundation for physical general intelligence.

We have built a world around the human body. Our tools, our homes, and the work we do all reflect it. Learning how people move through that world gives machines a place to begin.

Much of physical AI is being built on the assumption that intelligence must be taught one robot, one task, one environment at a time. We believe that starts in the wrong place. The richest source of physical intelligence is already here: people moving through the world.

That knowledge is already visible in countless hours of video. The challenge is turning what a camera sees into an understanding of action: how bodies move through space, interact with objects, and respond to the world around them. And from there, learning to imagine what a camera hasn’t yet seen, but might.

This is what we are building at Cartwheel.

We turn video into a form machines can learn better from. We build models that learn the patterns of human action and generate new movement. Our ambition is to carry that learning across bodies, from digital characters to robots acting in the physical world.

Our work began with the demands of animation, where a misplaced foot or an unnatural gesture can break the illusion of life. That attention to movement remains essential to everything we build.

We see a future where embodied intelligence is abundant, helping with both everyday and extraordinary tasks. Where people can bring imagined characters and worlds to life. Where a skill learned in one body can find new uses in another, and each advance gives us more possibilities for what we can do.

Humanity has spent a lifetime learning how to move.

We’re building the intelligence that can learn from us.`,
      },
    ],
  },
  team: [
    {
      id: 'jonathan-jarvis',
      name: 'Jonathan Jarvis',
      role: 'Co-founder & CEO',
    },
    {
      id: 'andrew-carr',
      name: 'Andrew Carr',
      role: 'Co-founder & Chief Scientist',
    },
    {
      id: 'joakim-olsson',
      name: 'Joakim Olsson',
      role: 'Principal Research Scientist',
    },
    {
      id: 'param-hanji',
      name: 'Param Hanji',
      role: 'Lead Research Scientist - Generative Vision',
    },
    {
      id: 'aalok-patwardhan',
      name: 'Aalok Patwardhan',
      role: 'Lead Research Scientist - Computer Vision',
    },
    { id: 'katie-lowen', name: 'Katie Lowen', role: 'Lead Data Engineer' },
    {
      id: 'gabriel-marquez',
      name: 'Gabriel Marquez',
      role: 'VP, Product & GTM Strategy',
    },
    { id: 'mitch-probst', name: 'Mitch Probst', role: 'Lead Engineer' },
    {
      id: 'buthaina-mahmoud',
      name: 'Buthaina Mahmoud',
      role: 'Lead Animation Engineer',
    },
    { id: 'steven-ziadie', name: 'Steven Ziadie', role: 'Design Lead' },
    { id: 'neil-helm', name: 'Neil Helm', role: 'Animation Lead' },
    { id: 'donald-chan', name: 'Donald Chan', role: 'Animation Lead' },
    { id: 'phillip-mason', name: 'Phillip Mason', role: 'Engineer' },
    {
      id: 'alex-krainin',
      name: 'Alex Krainin',
      role: 'Head of Strategy and Operations',
    },
    {
      id: 'vaniya-agrawal',
      name: 'Vaniya Agrawal',
      role: 'Senior Full-Stack Engineer',
    },
    {
      id: 'levi-harrison',
      name: 'Levi Harrison',
      role: 'Software Creative Technologist',
    },
    {
      id: 'sven-schmidt',
      name: 'Sven Schmidt',
      role: 'Senior Frontend Engineer',
    },
    { id: 'matthias-plappert', name: 'Matthias Plappert', role: 'Advisor' },
    { id: 'emiliano-gambaretto', name: 'Emiliano Gambaretto', role: 'Advisor' },
  ],
  investors: [
    {
      id: 'accel',
      name: 'Accel',
      media: {
        id: 'company.investor.accel',
        type: 'image',
        src: '/images/company-investors/accel.svg',
      },
    },
    {
      id: 'khosla-ventures',
      name: 'Khosla Ventures',
      media: {
        id: 'company.investor.khosla-ventures',
        type: 'image',
        src: '/images/company-investors/khosla-ventures.svg',
      },
    },
    {
      id: 'craft',
      name: 'Craft',
      media: {
        id: 'company.investor.craft',
        type: 'image',
        src: '/images/company-investors/craft.svg',
      },
    },
    {
      id: 'google',
      name: 'Google',
      media: {
        id: 'company.investor.google',
        type: 'image',
        src: '/images/company-investors/google.svg',
      },
    },
    {
      id: 'wndr',
      name: 'WNDR',
      media: {
        id: 'company.investor.wndr',
        type: 'image',
        src: '/images/company-investors/wndr.png',
      },
    },
    {
      id: 'tribeca-ventures',
      name: 'Tribeca Ventures',
      media: {
        id: 'company.investor.tribeca-ventures',
        type: 'image',
        src: '/images/company-investors/tribeca-ventures.svg',
      },
    },
    {
      id: 'human-ventures',
      name: 'Human Ventures',
      media: {
        id: 'company.investor.human-ventures',
        type: 'image',
        src: '/images/company-investors/human-ventures.svg',
      },
    },
    {
      id: 'deep-tech-venture-fund',
      name: 'Deep Tech Venture Fund',
      media: {
        id: 'company.investor.deep-tech-venture-fund',
        type: 'image',
        src: '/images/company-investors/deep-tech-venture-fund.png',
      },
    },
  ],
};
